import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Callout,
  ComparisonTable,
  MarketingPage,
  ProofStat,
  Section,
} from "@/components/marketing-page";
import { marketingHead, type FAQ } from "@/lib/seo";
import { MEASURED_SOURCE, GEO_STUDY } from "@/lib/measured-stats";
import { TOOL_FACTS } from "@/content/ai-tool-facts";
import { TIER_LIMITS } from "@/lib/tiers";

const PATH = "/cfo-guide-ai-visibility-roi";
const TITLE = "AI Visibility ROI & 2027 Budget Planning Guide";
const UPDATED_ISO = "2026-10-28";
const DESCRIPTION =
  "How much to budget for AI visibility in 2027 by company size, how to prove ROI to your CFO, a quarter-by-quarter plan and a one-page CFO summary template.";

/**
 * Sample behind the "vanity metric" objection this page answers.
 *
 * Measured, not estimated: 185 runs of the prompt "How do I prove AI
 * visibility ROI to my CFO?" across 49 scan batches, 2026-07-03 to
 * 2026-09-17. Update these numbers only from a fresh count of
 * opportunity_sources for that prompt, never to look better.
 */
const SAMPLE = {
  runs: 185,
  batches: 49,
  from: "July 3, 2026",
  to: "September 17, 2026",
  claudeRuns: 65,
  claudeSkeptical: 42,
  geminiRuns: 61,
  geminiVanity: 15,
  chatgptRuns: 54,
  chatgptSkeptical: 6,
} as const;

const FAQS: FAQ[] = [
  {
    q: "How much should you budget for AI visibility in 2027?",
    a: "Software is the smaller line. At prices published in October 2026, tracking runs from $0 (free plans at Monroya and AthenaHQ) to $79-$199 a month for a small business or B2B team, $199-$489 a month for mid-market teams, and custom contracts at the enterprise end (Profound). That is $0 to about $5,900 a year before enterprise. Budget separately for the staff or agency time to act on what tracking finds, using your own rates.",
  },
  {
    q: "Which AI visibility metrics are CFO-ready?",
    a: "Bring the rate of answered buying questions that name your business, with the question set, run count and dates; AI-referred and self-reported leads; qualified opportunities; and closed-won revenue with gross profit. Keep these separate: an answer rate is an observed visibility metric, pipeline is not revenue, and an estimated opportunity is not a sale. Monroya supplies recorded answers and sources; your analytics and sales records supply the commercial outcomes.",
  },
  {
    q: "How do I calculate revenue at risk from low AI visibility?",
    a: "For a scenario estimate, multiply estimated monthly buyer conversations by the gap between target and current naming rates, then by your lead-to-customer close rate and average sale value. Express rates as decimals. For example, 200 conversations × 0.20 gap × 0.10 close rate × $1,000 sale value = $4,000 in potential sales value per month. These are illustrative inputs, not a customer result. The model assumes missing visibility could become qualified leads; it does not prove lost revenue. Recorded answer counts do not measure market demand.",
  },
  {
    q: "How do I prove realized ROI rather than an opportunity estimate?",
    a: "Use attributable gross profit, not answer counts: ROI = (attributable gross profit − total program cost) ÷ total program cost. Include software, content work and staff time. Reconcile leads to closed sales, document the attribution method and compare against a dated baseline. A scan can confirm that AI named or cited your business; it cannot establish that Monroya caused a sale. Without linked sales evidence, report visibility movement and ROI as unproven.",
  },
  {
    q: "What evidence should a local-business implementation result include?",
    a: "Report a dated before-and-after naming rate on unchanged buyer questions, qualified leads linked to AI referrals or customer-reported sources, and closed sales or gross profit from those leads. Give the counts behind each rate, the measurement period, program cost and any attribution limits. Publish a named customer result only with permission and supporting records. A generated draft or a hypothetical example is not evidence of a successful implementation.",
  },
  {
    q: "Is AI visibility a vanity metric?",
    a: "Presence in real buying questions can be a useful leading indicator, but it is not revenue proof. Track the share of answered buying-stage questions that name you rather than mentions anywhere, then compare it with separately recorded leads and sales. An answer that omits your name does not establish that a buyer or a sale was lost.",
  },
  {
    q: "How is AI visibility ROI actually measured?",
    a: "Keep a stable set of buyer questions, run repeat checks and record the share of answered questions that name you, with counts and dates. That measures visibility, not financial ROI. Use your own lead and sales records to calculate attributable gross profit against total program cost. A calculation using estimated conversation volume, naming-rate change, close rate and sale value is an opportunity scenario, not measured revenue.",
  },
  {
    q: "What attribution can a CFO actually rely on here?",
    a: "Two honest layers. The first is direct: referral traffic from assistant domains and self-reported sourcing on inbound forms, both small and both undercounted. The second is the measured share itself, treated the way finance already treats a leading indicator such as pipeline coverage. Claiming clean last-touch attribution from an AI answer would be dishonest, and a CFO will catch it.",
  },
  {
    q: "How long before a visibility change shows up?",
    a: "Retrieval-driven gains, where an assistant reads a new or corrected page live, typically appear within days to a few weeks. Gains inside a model's trained knowledge arrive on refresh cycles measured in months. Set the review at one quarter, not one week, and judge the trend rather than any single scan.",
  },
  {
    q: "What does it cost to do nothing?",
    a: "You can size a potential opportunity using estimated buyer conversations, a naming-rate gap, close rate and sale value, but you cannot treat every missing answer as a lost lead. Track incorrect descriptions separately and verify them against your business facts. Without linked customer and sales records, neither absence nor a wrong answer proves a specific financial loss.",
  },
  {
    q: "What is the smallest credible first measurement?",
    a: "Ten to fifteen buying questions your sales team already hears, run several times each against the assistants your buyers use, repeated monthly. That is enough to establish a baseline share and see movement. A single run is not a measurement, because model output varies enough between runs to invent movement that never happened.",
  },
];

export const Route = createFileRoute("/cfo-guide-ai-visibility-roi")({
  head: () =>
    marketingHead({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "AI Visibility ROI & 2027 Budget Planning Guide", path: PATH },
      ],
      faqs: FAQS,
      dateModified: UPDATED_ISO,
    }),
  component: Page,
});

function Page() {
  return (
    <MarketingPage
      stamp="2027 planning guide"
      title="AI Visibility ROI & 2027 Budget Planning Guide"
      dek="For the conversation where marketing has to set and defend next year's AI visibility budget with finance. Budget ranges, the ROI case, a quarter-by-quarter plan and a one-page summary you can copy."
      updated="October 28, 2026"
      faqs={FAQS}
      faqTitle="CFO-Ready Metrics"
      related={[
        {
          label: "AI Visibility ROI Calculator",
          to: "/roi-calculator",
          description: "Size the gap with your own contract value",
        },
        {
          label: "What is AI Share of Voice",
          to: "/ai-share-of-voice",
          description: "The measurement framework behind the numbers",
        },
        {
          label: "2026 AI Visibility Benchmark",
          to: "/benchmark",
          description: "Where 60 measured companies actually landed",
        },
      ]}
    >
      {/* Front-loaded answer */}
      <section className="not-prose">
        <p className="text-lg leading-relaxed text-ink">
          <strong>Estimated revenue at risk = monthly buyer conversations ×
          (target naming rate − current naming rate) × lead-to-customer close
          rate × average sale value.</strong> Use a positive gap and express
          rates as decimals. For a local business, illustrative inputs of 200
          conversations, a 20-percentage-point gap, a 10% close rate and a
          $1,000 sale value produce a $4,000 monthly opportunity estimate.
          This is not a customer outcome or proven lost revenue: it assumes
          visibility gaps could become qualified leads. Monroya measures
          recorded answers and cited sources, not market demand or sales.
          Bring your own lead and sales records to test the financial case.
        </p>
      </section>

      <BudgetSection />

      <Section id="prove" heading="How do you prove AI visibility ROI to your CFO?">
        <p>
          Separate what is measured from what is estimated. Measured: the share
          of answered buying questions that name you, per assistant, with run
          counts and dates. Estimated: the revenue attached to that share. The
          sections below cover the objection you will hear, the chain finance
          will check, and the four numbers to bring.
        </p>
      </Section>

      <Section
        id="objection"
        heading="The objection is real, and here is the size of it"
      >
        <p>
          When we ask the assistants themselves how to prove AI visibility ROI
          to a CFO, a large share of the answers push back on the premise. That
          is not a guess from a handful of tries. We have run the question{" "}
          {SAMPLE.runs} times across {SAMPLE.batches} separate scan batches
          between {SAMPLE.from} and {SAMPLE.to}.
        </p>
        <div className="not-prose my-6 grid gap-4 sm:grid-cols-3">
          <ProofStat
            value={`${SAMPLE.claudeSkeptical} of ${SAMPLE.claudeRuns}`}
            label="Claude answers calling the category hard to prove or immature"
            source={`Monroya scan data, ${SAMPLE.from} to ${SAMPLE.to}`}
          />
          <ProofStat
            value={`${SAMPLE.geminiVanity} of ${SAMPLE.geminiRuns}`}
            label="Gemini answers using the words vanity metric outright"
            source={`Monroya scan data, ${SAMPLE.from} to ${SAMPLE.to}`}
          />
          <ProofStat
            value={`${SAMPLE.chatgptSkeptical} of ${SAMPLE.chatgptRuns}`}
            label="ChatGPT answers skeptical; the rest answer with a framework"
            source={`Monroya scan data, ${SAMPLE.from} to ${SAMPLE.to}`}
          />
        </div>
        <p>
          Two things follow. First, if your CFO calls this a vanity metric, the
          objection is not personal and it is not uninformed: it is the
          consensus framing your CFO will hear from the same assistants your
          team uses. Second, the objection is answerable, because it is about
          attribution and maturity rather than about whether buyers use these
          tools.
        </p>
        <Callout label="What the objection gets right">
          Clean last-touch attribution from an AI answer to a closed deal does
          not exist today. Do not claim it. The honest case treats measured
          answer share as a leading indicator, the way finance already treats
          pipeline coverage or win-rate-by-stage, and pairs it with the small,
          direct referral numbers that can be observed.
        </Callout>
      </Section>

      <Section
        id="chain"
        heading="The measurable chain, in the order finance will check it"
      >
        <p>
          Each step is a number someone can question, which is the point. A
          case built on one blended figure invites the vanity-metric reply. A
          case built on four separate terms invites an argument about one of
          them, which is a far better conversation to be in.
        </p>
        <ol className="list-decimal space-y-2 pl-5">
          <li>
            <strong>Buying questions per month.</strong> How many purchase
            conversations your category may generate. Label the estimate and
            its source; demo requests and sales-team questions can inform it,
            but recorded AI answer counts do not establish market demand.
          </li>
          <li>
            <strong>Share of those answers naming you.</strong> Measured on a
            fixed prompt set across the assistants your buyers use, several
            runs each, on a fixed cadence.
          </li>
          <li>
            <strong>Close rate on AI-sourced conversations.</strong> Your
            existing close rate is the defensible starting point. Do not assume
            these buyers convert better without evidence.
          </li>
          <li>
            <strong>Average contract value.</strong> Finance already owns this
            number, so nobody argues about it.
          </li>
        </ol>
        <p>
          Multiply them for a scenario estimate of the opportunity between
          your current and target naming rates, not proven lost revenue. The{" "}
          <Link to="/roi-calculator" className="underline">
            ROI calculator
          </Link>{" "}
          does the arithmetic with your own inputs and keeps everything in the
          browser.
        </p>
      </Section>

      <Section
        id="what-moves-it"
        heading="What actually moves citations, per unit of effort"
      >
        <p>
          This is the table finance responds to, because it compares two things
          a content budget can buy and shows that they are not equally
          productive. The measured effect is on citation and visibility rates,
          not on revenue directly.
        </p>
        <div className="not-prose my-6">
          <ComparisonTable
            headers={[
              "What you fund",
              "Effort per page",
              "Measured effect on AI citations",
              "Why it behaves that way",
            ]}
            rows={[
              [
                "Adding hard statistics and sourced numbers to pages you already have",
                "Low. An edit pass on existing pages, no new page production.",
                "Up to a 40% improvement in visibility in generative engine answers",
                "Assistants quote specific, checkable claims. A sourced number is liftable as a sentence; an adjective is not.",
              ],
              [
                "Adding word count to the same pages",
                "High. Net-new writing, review, and approval for every page.",
                "No measured lift in the same study",
                "Length does not make a passage more quotable. Retrieval selects the citable claim, not the longest section.",
              ],
              [
                "Structuring question-and-answer content with matching FAQ markup",
                "Low to moderate. Rewriting existing content into real questions and answers.",
                "Not separately measured. Our own scans show answers are assembled from many domains, and question-shaped pages are the ones quoted whole.",
                "Buyer questions arrive as questions. A page already shaped as the answer needs no reassembly.",
              ],
            ]}
          />
          <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            Source:{" "}
            <a
              href={GEO_STUDY.url}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {GEO_STUDY.source}
            </a>
            . Scan figures: {MEASURED_SOURCE}.
          </p>
        </div>
        <p>
          The budget implication is the part to put in the deck. The cheaper of
          the two interventions is the one with the measured effect, so the
          first quarter of this work is mostly an edit pass on pages that
          already exist, not a content production line.
        </p>
      </Section>

      <Section id="bring" heading="The four numbers to bring to the meeting">
        <p>
          Walk in with these, sourced and dated. Leave projections out of the
          first conversation entirely.
        </p>
        <ol className="list-decimal space-y-2 pl-5">
          <li>
            <strong>Your measured share today,</strong> per assistant, on a
            named prompt set, with the run count and date range attached.
          </li>
          <li>
            <strong>The competitors named instead of you,</strong> with the
            count of answers each one appeared in. This is usually the slide
            that ends the vanity-metric discussion.
          </li>
          <li>
            <strong>The revenue attached to the gap,</strong> calculated from
            your own volume, close rate, and contract value, presented as a
            sizing estimate rather than a forecast.
          </li>
          <li>
            <strong>The cost and duration of the test,</strong> with a review
            date one quarter out and a stated condition for stopping.
          </li>
        </ol>
        <Callout label="Say this out loud in the meeting">
          This is a leading indicator with a measurable input and an honest
          attribution gap. We are asking to fund one quarter of measurement and
          an edit pass, then to review the measured share against the
          competitor set before spending anything more.
        </Callout>
      </Section>

      <PlanSection />

      <SummaryTemplate />

      <Section id="print" heading="Using this guide">
        <p>
          Nothing here is gated. Print the page or save it as a PDF from your
          browser to bring to the meeting, and send the link to anyone who needs
          it. To replace the estimates with your own measured numbers, run a
          scan of your buyer questions and bring the resulting share figure
          instead.
        </p>
        <p>
          <Link to="/check" className="underline">
            Run a free visibility check
          </Link>{" "}
          or{" "}
          <Link to="/roi-calculator" className="underline">
            size the gap in the calculator
          </Link>
          .
        </p>
      </Section>
    </MarketingPage>
  );
}

const PRICE = (name: string) => TOOL_FACTS.find((t) => t.name === name);

function BudgetSection() {
  const otterly = PRICE("Otterly.ai");
  const peec = PRICE("Peec AI");
  const scrunch = PRICE("Scrunch AI");
  const athena = PRICE("AthenaHQ");
  const profound = PRICE("Profound");
  return (
    <Section id="budget" heading="How much should you budget for AI visibility in 2027?">
      <p>
        Plan two lines: tracking software, and the time to act on what it
        finds. Software prices below are the published monthly prices on each
        vendor's site on the dates shown. Annual figures are the monthly price
        times twelve, before any annual discount. Staff and agency time is
        yours to fill in; we do not publish an estimate for it.
      </p>
      <div className="not-prose my-6">
        <ComparisonTable
          headers={["Company size", "Typical software range", "Per year", "Prices this is based on"]}
          rows={[
            [
              "Solo or local business (1-10 people)",
              "$0-$79 a month",
              "$0-$948",
              `Monroya ${TIER_LIMITS.trial.label} (${TIER_LIMITS.trial.maxPrompts} questions, ${TIER_LIMITS.trial.cadence}) or ${TIER_LIMITS.starter.label} ${TIER_LIMITS.starter.price}; Otterly.ai from $29 (${otterly?.checked})`,
            ],
            [
              "Small B2B or growing team (10-50 people)",
              "$79-$199 a month",
              "$948-$2,388",
              `Monroya ${TIER_LIMITS.starter.price} or ${TIER_LIMITS.growth.label} ${TIER_LIMITS.growth.price}; Peec AI ${peec?.pricing} (${peec?.checked}); Otterly.ai $189`,
            ],
            [
              "Mid-market (50-500 people)",
              "$199-$489 a month",
              "$2,388-$5,868",
              `Monroya ${TIER_LIMITS.growth.price} or ${TIER_LIMITS.pro.label} ${TIER_LIMITS.pro.price}; Scrunch AI ${scrunch?.pricing} (${scrunch?.checked}); AthenaHQ $295 (${athena?.checked}); Otterly.ai $489`,
            ],
            [
              "Enterprise (500+ people)",
              "Custom",
              "Quoted",
              `${profound?.name}: ${profound?.pricing} (${profound?.checked})`,
            ],
          ]}
        />
        <p className="mt-3 text-[13px] text-muted-foreground">
          Monroya prices are from the live{" "}
          <Link to="/pricing" className="underline">pricing page</Link>. Competitor
          prices change; check each vendor's site before you sign. Full
          comparison:{" "}
          <Link to="/best-ai-visibility-tools" className="underline">best AI visibility tools</Link>.
        </p>
      </div>
      <p>
        A sensible 2027 request is the smallest plan that covers the buying
        questions your sales team already hears, for one quarter, with a review
        date. Move up a plan only if the first quarter shows gaps you can act on.
      </p>
    </Section>
  );
}

const QUARTERS: Array<{ q: string; focus: string; output: string }> = [
  {
    q: "Q1 2027",
    focus: "Baseline. Pick 10-50 buying questions, run them on the assistants your buyers use, record who is named instead of you.",
    output: "Dated naming rate per assistant and the competitor list.",
  },
  {
    q: "Q2 2027",
    focus: "Fix what already exists. Add sourced numbers and direct answers to pages you have; correct wrong facts about you.",
    output: "Edited pages, before-and-after naming rate on the same questions.",
  },
  {
    q: "Q3 2027",
    focus: "Fill the gaps. Create pages for questions where no page of yours answers, and get listed on the sources assistants cite.",
    output: "New pages and listings, with the cited sources they target.",
  },
  {
    q: "Q4 2027",
    focus: "Review against money. Match AI-referred and self-reported leads to closed sales; decide the 2028 budget.",
    output: "Attributable gross profit vs. program cost, or a clear statement that ROI is still unproven.",
  },
];

function PlanSection() {
  return (
    <Section id="plan" heading="What should a 2027 AI visibility plan include?">
      <p>
        One measurable goal per quarter, each reviewed on the same question set
        so movement is real and not a new baseline.
      </p>
      <div className="not-prose my-6">
        <ComparisonTable
          headers={["Quarter", "Focus", "What you bring to the review"]}
          rows={QUARTERS.map((r) => [r.q, r.focus, r.output])}
        />
      </div>
    </Section>
  );
}

function SummaryTemplate() {
  return (
    <Section id="cfo-summary" heading="One-page CFO summary template">
      <p>Copy this into a doc and fill in the brackets. Keep it to one page.</p>
      <div className="not-prose my-6 rounded-[6px] border border-border bg-panel p-5 text-[14px] leading-relaxed text-ink">
        <p><strong>Request:</strong> [plan name] at [$ per month] for [3] months, review on [date].</p>
        <p className="mt-2"><strong>What we measured:</strong> AI assistants named us in [x] of [y] answered buying questions ([z]%), [start date] to [end date], across [assistants].</p>
        <p className="mt-2"><strong>Who they named instead:</strong> [competitor] in [n] answers, [competitor] in [n] answers.</p>
        <p className="mt-2"><strong>Sizing estimate (not a forecast):</strong> [monthly buyer conversations] × [naming-rate gap] × [close rate] × [average sale] = [$ per month].</p>
        <p className="mt-2"><strong>What we will do:</strong> [edit pass on existing pages / new pages for missing questions / listings on cited sources].</p>
        <p className="mt-2"><strong>How we will judge it:</strong> same questions re-checked on [date]; AI-referred leads and closed sales from [CRM]; stop if [condition].</p>
        <p className="mt-2"><strong>Total cost:</strong> software [$] + staff or agency time [$] = [$].</p>
      </div>
    </Section>
  );
}
